import { decoder, formatToIso, hasItems, parseISODateRange } from "@repo/utils";
import { parseJSON } from "date-fns";
import qs from "qs";
import useSWRInfinite from "swr/infinite";

import { type ApiError } from "../ApiError";
import { type Customer } from "../customer/types";
import * as fetcher from "../fetcher";
import { mutateWithStartKey } from "../mutateWithStartKey";
import { type NbaType } from "../nextBestAction/types";
import type {
  InstallationQuotationStatus,
  InstallationType,
  OpportunitiesSearchFilters,
  OpportunitiesSearchOptions,
  OpportunitiesSearchResponse,
  Opportunity,
  OpportunitySearch,
  OpportunityType,
  WorkOrderStatus,
  WorkOrderType,
  hfb,
} from "./types";

export function opportunitiesSearchFiltersFromString(
  filterString: string
): OpportunitiesSearchFilters {
  const parsedFilters = qs.parse(filterString, {
    decoder,
  });

  const filters: OpportunitiesSearchFilters = {};

  if (
    parsedFilters.opportunityTypeFilter &&
    typeof parsedFilters.opportunityTypeFilter === "string"
  ) {
    filters.opportunityType = parsedFilters.opportunityTypeFilter.split(
      ","
    ) as OpportunityType[];
  }

  if (
    parsedFilters.classificationFilter &&
    typeof parsedFilters.classificationFilter === "string"
  ) {
    filters.classification = parsedFilters.classificationFilter.split(
      ","
    ) as Opportunity["classification"][];
  }

  if (
    parsedFilters.statusFilter &&
    typeof parsedFilters.statusFilter === "string"
  ) {
    filters.status = parsedFilters.statusFilter.split(",");
  }

  if (parsedFilters.hfbFilter && typeof parsedFilters.hfbFilter === "string") {
    filters.department = parsedFilters.hfbFilter.split(",") as hfb[];
  }

  if (parsedFilters.buFilter && typeof parsedFilters.buFilter === "string") {
    filters.businessUnit = parsedFilters.buFilter.split(",");
  } else if (
    parsedFilters.buFilter &&
    typeof parsedFilters.buFilter === "number"
  ) {
    filters.businessUnit = [parsedFilters.buFilter.toString().padStart(3, "0")];
  }

  if (
    parsedFilters.customerTypeFilter &&
    typeof parsedFilters.customerTypeFilter === "string"
  ) {
    filters.customerType = parsedFilters.customerTypeFilter.split(
      ","
    ) as Customer["customerType"][];
  }

  if (
    parsedFilters.serviceFilter &&
    typeof parsedFilters.serviceFilter === "string"
  ) {
    filters.services = parsedFilters.serviceFilter.split(",").map(Number);
  } else if (typeof parsedFilters.serviceFilter === "number") {
    filters.services = [parsedFilters.serviceFilter];
  }

  if (
    parsedFilters.valueFilter &&
    typeof parsedFilters.valueFilter === "string"
  ) {
    const values = parsedFilters.valueFilter.split("-");
    filters.amount = [];
    if (values.length > 0) {
      filters.amount[0] = Number(values[0]);
    }
    if (values.length > 1) {
      filters.amount[1] = Number(values[1]);
    }
  }

  if (
    parsedFilters.dateCreatedFilter &&
    typeof parsedFilters.dateCreatedFilter === "string"
  ) {
    const { fromDate, toDate } = parseISODateRange(
      parsedFilters.dateCreatedFilter
    );
    filters.createdDate = [];

    if (fromDate && fromDate.length > 0) {
      filters.createdDate[0] = parseJSON(fromDate);
    }
    if (toDate && toDate.length > 1) {
      filters.createdDate[1] = parseJSON(toDate);
    }
  }

  if (
    parsedFilters.dateUpdatedFilter &&
    typeof parsedFilters.dateUpdatedFilter === "string"
  ) {
    const { fromDate, toDate } = parseISODateRange(
      parsedFilters.dateUpdatedFilter
    );
    filters.updatedDate = [];

    if (fromDate && fromDate.length > 0) {
      filters.updatedDate[0] = parseJSON(fromDate);
    }
    if (toDate && toDate.length > 1) {
      filters.updatedDate[1] = parseJSON(toDate);
    }
  }

  if (
    parsedFilters.followUpConsentFilter &&
    typeof parsedFilters.followUpConsentFilter === "string"
  ) {
    filters.followUpConsentFilter = parsedFilters.followUpConsentFilter;
  }

  if (
    parsedFilters.promotionalConsentFilter &&
    typeof parsedFilters.promotionalConsentFilter === "string"
  ) {
    filters.promotionalConsentFilter = parsedFilters.promotionalConsentFilter;
  }

  if (parsedFilters.term && typeof parsedFilters.term === "string") {
    filters.term = parsedFilters.term;
  }

  if (parsedFilters.teamsFilter) {
    if (typeof parsedFilters.teamsFilter === "string") {
      filters.teamFilter = Number(parsedFilters.teamsFilter);
    } else if (typeof parsedFilters.teamsFilter === "number") {
      filters.teamFilter = parsedFilters.teamsFilter;
    }
  }

  if (
    parsedFilters.assignedToTeam &&
    typeof parsedFilters.assignedToTeam === "boolean"
  ) {
    filters.assignedToTeam = parsedFilters.assignedToTeam;
  }

  if (parsedFilters.coworkerFilter) {
    if (typeof parsedFilters.coworkerFilter === "string") {
      filters.coworkerFilter = parsedFilters.coworkerFilter.split(",");
    }
  }

  if (parsedFilters.tags && typeof parsedFilters.tags === "string") {
    filters.tags = parsedFilters.tags.split(",");
  }

  if (parsedFilters.nbaTypes && typeof parsedFilters.nbaTypes === "string") {
    filters.nbaTypes = parsedFilters.nbaTypes.split(",") as NbaType[];
  }

  if (
    parsedFilters.orderStatus &&
    typeof parsedFilters.orderStatus === "string"
  ) {
    filters.orderStatus = parsedFilters.orderStatus.split(",");
  }

  if (
    parsedFilters.orderMethod &&
    typeof parsedFilters.orderMethod === "string"
  ) {
    filters.orderMethod = parsedFilters.orderMethod.split(",");
  }

  if (
    parsedFilters.bokaServiceFilter &&
    typeof parsedFilters.bokaServiceFilter === "string"
  ) {
    filters.bokaServices = parsedFilters.bokaServiceFilter.split(",");
  }

  if (
    parsedFilters.installationQuotationStatusFilter &&
    typeof parsedFilters.installationQuotationStatusFilter === "string"
  ) {
    filters.installationQuotationStatus =
      parsedFilters.installationQuotationStatusFilter.split(
        ","
      ) as InstallationQuotationStatus[];
  }

  if (
    parsedFilters.installationQuotationExistsFilter &&
    typeof parsedFilters.installationQuotationExistsFilter === "boolean"
  ) {
    filters.installationQuotationExists =
      parsedFilters.installationQuotationExistsFilter;
  }

  if (
    parsedFilters.serviceWorkOrderStatusFilter &&
    typeof parsedFilters.serviceWorkOrderStatusFilter === "string"
  ) {
    filters.workOrderStatus = parsedFilters.serviceWorkOrderStatusFilter.split(
      ","
    ) as WorkOrderStatus[];
  }

  if (
    parsedFilters.serviceWorkOrderWorkTypeFilter &&
    typeof parsedFilters.serviceWorkOrderWorkTypeFilter === "string"
  ) {
    filters.workOrderType = parsedFilters.serviceWorkOrderWorkTypeFilter.split(
      ","
    ) as WorkOrderType[];
  }

  if (
    parsedFilters.isNoActionPlanned &&
    typeof parsedFilters.isNoActionPlanned === "boolean"
  ) {
    filters.isNoActionPlanned = parsedFilters.isNoActionPlanned;
  }

  if (
    parsedFilters.projectIds &&
    typeof parsedFilters.projectIds === "string"
  ) {
    filters.projectIds = parsedFilters.projectIds.split(",");
  }

  if (
    parsedFilters.installationType &&
    typeof parsedFilters.installationType === "string"
  ) {
    filters.installationType = parsedFilters.installationType.split(
      ","
    ) as InstallationType[];
  }

  if (
    parsedFilters.installationDate &&
    typeof parsedFilters.installationDate === "string"
  ) {
    const { fromDate, toDate } = parseISODateRange(
      parsedFilters.installationDate
    );
    filters.installationDate = [];

    if (fromDate && fromDate.length > 0) {
      filters.installationDate[0] = parseJSON(fromDate);
    }
    if (toDate && toDate.length > 1) {
      filters.installationDate[1] = parseJSON(toDate);
    }
  }

  if (
    parsedFilters.isColdProject &&
    typeof parsedFilters.isColdProject === "boolean"
  ) {
    filters.isColdProject = parsedFilters.isColdProject;
  }

  if (
    parsedFilters.stageFilter &&
    typeof parsedFilters.stageFilter === "string"
  ) {
    filters.stageFilter = parsedFilters.stageFilter.split(",");
  }

  if (
    parsedFilters.waitingList &&
    typeof parsedFilters.waitingList === "boolean"
  ) {
    filters.waitingList = parsedFilters.waitingList;
  }

  if (
    parsedFilters.budgetFilter &&
    typeof parsedFilters.budgetFilter === "string"
  ) {
    const values = parsedFilters.budgetFilter.split("-");
    filters.budgetFilter = [];
    if (values.length > 0) {
      filters.budgetFilter[0] = Number(values[0]);
    }
    if (values.length > 1) {
      filters.budgetFilter[1] = Number(values[1]);
    }
  }

  if (
    parsedFilters.expectedPurchaseDateFilter &&
    typeof parsedFilters.expectedPurchaseDateFilter === "string"
  ) {
    const { fromDate, toDate } = parseISODateRange(
      parsedFilters.expectedPurchaseDateFilter
    );
    filters.expectedPurchaseDateFilter = [];

    if (fromDate && fromDate.length > 0) {
      filters.expectedPurchaseDateFilter[0] = parseJSON(fromDate);
    }
    if (toDate && toDate.length > 1) {
      filters.expectedPurchaseDateFilter[1] = parseJSON(toDate);
    }
  }

  return filters;
}

export function stringifyOpportunitySearchQuery(
  filters?: OpportunitiesSearchFilters,
  options?: OpportunitiesSearchOptions
): string {
  if (!filters || !options) {
    return "";
  }

  const query: Record<string, any> = {
    pageIndex: options.pageIndex ?? 0,
    pageSize: options?.pageSize ?? 10,
    sortKey: "",
  };

  if (options?.isPinned) {
    query.sortKey += "isPinned|DESC";
  }

  if (options?.sortKey) {
    query.sortKey += `${
      query.sortKey.length > 0 ? "," : ""
    }${options?.sortKey}|${options?.sortOrder?.toUpperCase() ?? "ASC"}`;
  }

  if (hasItems(filters.opportunityType)) {
    query.opportunityTypeFilter = filters.opportunityType.join(",");
  }

  if (hasItems(filters.classification)) {
    query.classificationFilter = filters.classification
      .map((id) => id.toUpperCase())
      .join(",");
  }

  if (hasItems(filters.status)) {
    query.statusFilter = filters.status.join(",");
  }

  if (hasItems(filters.department)) {
    query.hfbFilter = filters.department.join(",");
  }

  if (hasItems(filters.businessUnit)) {
    query.buFilter = filters.businessUnit.join(",");
  }

  if (hasItems(filters.customerType)) {
    query.customerTypeFilter = filters.customerType.join(",");
  }

  if (hasItems(filters.services)) {
    query.serviceFilter = filters.services.join(",");
  }

  if (hasItems(filters.amount)) {
    query.valueFilter = `${Math.floor(filters.amount[0])}-${Math.ceil(
      filters.amount[1]
    )}`;
  }

  if (hasItems(filters.createdDate)) {
    if (filters.createdDate?.[0] && filters.createdDate?.[1]) {
      const fromDate = filters.createdDate[0];
      const toDate = filters.createdDate[1];

      query.dateCreatedFilter = `${formatToIso(fromDate)}-${formatToIso(toDate)}`;
    }
  }

  if (hasItems(filters.updatedDate)) {
    if (filters.updatedDate?.[0] && filters.updatedDate?.[1]) {
      const fromDate = filters.updatedDate[0];
      const toDate = filters.updatedDate[1];

      query.dateUpdatedFilter = `${formatToIso(fromDate)}-${formatToIso(toDate)}`;
    }
  }

  if (filters.followUpConsentFilter) {
    query.followUpConsentFilter = filters.followUpConsentFilter;
  }

  if (filters.promotionalConsentFilter) {
    query.promotionalConsentFilter = filters.promotionalConsentFilter;
  }

  if (filters.term) {
    query.searchTerm = filters.term;
  }

  if (filters.teamFilter) {
    query.teamsFilter = filters.teamFilter;
  }

  if (filters.assignedToTeam) {
    query.assignedToTeam = filters.assignedToTeam;
  }

  if (hasItems(filters.coworkerFilter)) {
    query.coworkerFilter = filters.coworkerFilter.join(",");
  }

  if (hasItems(filters.tags)) {
    query.tags = filters.tags.join(",");
  }

  if (hasItems(filters.nbaTypes)) {
    query.nbaTypes = filters.nbaTypes.join(",");
  }

  if (hasItems(filters.orderStatus)) {
    query.orderStatus = filters.orderStatus.join(",");
  }

  if (hasItems(filters.customerLocales)) {
    query.customerLocales = filters.customerLocales.join(",");
  }

  if (hasItems(filters.orderMethod)) {
    query.orderMethod = filters.orderMethod.join(",");
  }

  if (hasItems(filters.bokaServices)) {
    query.bokaServiceFilter = filters.bokaServices.join(",");
  }

  if (hasItems(filters.installationQuotationStatus)) {
    query.installationQuotationStatusFilter =
      filters.installationQuotationStatus.join(",");
  }

  if (filters.installationQuotationExists !== undefined) {
    query.installationQuotationExistsFilter =
      filters.installationQuotationExists;
  }

  if (hasItems(filters.workOrderStatus)) {
    query.serviceWorkOrderStatusFilter = filters.workOrderStatus.join(",");
  }

  if (hasItems(filters.workOrderType)) {
    query.serviceWorkOrderWorkTypeFilter = filters.workOrderType.join(",");
  }

  if (filters.isWidgetSearch) {
    query.isWidgetSearch = filters.isWidgetSearch;
  }

  if (filters.isColdProject) {
    query.isColdProject = filters.isColdProject;
  }

  if (filters.isNoActionPlanned === true) {
    query.isNoActionPlanned = true;
  }

  if (filters.followupAssignee) {
    query.followupAssignee = filters.followupAssignee;
  }

  if (filters.projectIds) {
    query.projectIds = filters?.projectIds.join(",");
  }

  if (hasItems(filters.installationType)) {
    query.installationType = filters?.installationType.join(",");
  }

  if (hasItems(filters.stageFilter)) {
    query.stageFilter = filters.stageFilter.join(",");
  }

  if (filters.installationDate) {
    if (filters.installationDate?.[0] && filters.installationDate?.[1]) {
      const fromDate = filters.installationDate[0];
      const toDate = filters.installationDate[1];

      query.installationDate = `${formatToIso(fromDate)}-${formatToIso(toDate)}`;
    }
  }

  if (filters?.waitingList !== undefined) {
    query.waitingListOrderExistsFilter = filters.waitingList;
  }

  if (hasItems(filters.budgetFilter)) {
    query.budgetFilter = `${Math.floor(filters.budgetFilter[0])}-${Math.ceil(
      filters.budgetFilter[1]
    )}`;
  }

  if (hasItems(filters.expectedPurchaseDateFilter)) {
    if (
      filters.expectedPurchaseDateFilter?.[0] &&
      filters.expectedPurchaseDateFilter?.[1]
    ) {
      const fromDate = filters.expectedPurchaseDateFilter[0];
      const toDate = filters.expectedPurchaseDateFilter[1];

      query.expectedPurchaseDateFilter = `${formatToIso(fromDate)}-${formatToIso(toDate)}`;
    }
  }

  const searchParams = new URLSearchParams(query);
  return searchParams.toString();
}

export const getOpportunitiesSearchKeyCreator =
  (
    filters: OpportunitiesSearchFilters | null,
    options: OpportunitiesSearchOptions
  ) =>
  (pageIndex: number) => {
    if (!filters) {
      return null;
    }
    const query = stringifyOpportunitySearchQuery(filters, {
      ...options,
      pageIndex,
    });

    return `/opportunities/search?${query}`;
  };
export const useOpportunitiesSearch = (
  filters: OpportunitiesSearchFilters | null,
  options: OpportunitiesSearchOptions
) => {
  return useSWRInfinite<OpportunitiesSearchResponse, ApiError>(
    getOpportunitiesSearchKeyCreator(filters, options),
    (url) =>
      fetcher.core.get(url).then((res) => {
        mutateWithStartKey("/coworkers/filters");
        return res as OpportunitiesSearchResponse;
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: options.refreshResult ? 60 * 1000 : undefined,
    }
  );
};

export const mapSearchResultToOpportunityArray = (
  data: OpportunitiesSearchResponse[] | undefined,
  filterCurrentProject?: number
): OpportunitySearch[] => {
  const result = data?.flatMap((d) => d.content) ?? [];

  return filterCurrentProject
    ? result.filter((o) => o.opportunityId !== filterCurrentProject)
    : result;
};

export const useSuccessStoriesSearch = (
  filters: OpportunitiesSearchFilters | null,
  options: OpportunitiesSearchOptions
) => {
  return useSWRInfinite<OpportunitiesSearchResponse, ApiError>(
    getOpportunitiesSearchKeyCreator(filters, options),
    (url) =>
      fetcher.core.get(url).then((res) => {
        return res as OpportunitiesSearchResponse;
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: undefined,
      shouldRetryOnError: false,
    }
  );
};
